import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { getPacks, startCheckout, paypalConfig, paypalCreateOrder, paypalCaptureOrder } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { useLang } from "../../i18n/LanguageContext";
import { toast } from "sonner";
import { Coins, Loader2, Zap, Sparkles } from "lucide-react";

const META = {
  pack_100: { icon: Coins, accent: "text-blue-300" },
  pack_600: { icon: Zap, accent: "text-blue-200", featured: true },
  pack_2000: { icon: Sparkles, accent: "text-blue-100" },
};

const PayPalPackButton = ({ pack, onDone }) => (
  <PayPalButtons
    fundingSource="paypal"
    style={{ layout: "horizontal", color: "gold", shape: "pill", height: 40, tagline: false, label: "pay" }}
    createOrder={async () => {
      const { data } = await paypalCreateOrder(pack.lookup_key);
      return data.order_id;
    }}
    onApprove={async (data) => {
      try {
        const { data: res } = await paypalCaptureOrder(data.orderID);
        if (res.status === "COMPLETED") {
          toast.success(`Paiement réussi ! ${pack.credits} crédits ajoutés.`);
          onDone && onDone();
        } else {
          toast.error("Paiement non complété.");
        }
      } catch {
        toast.error("Échec de la capture du paiement PayPal.");
      }
    }}
    onError={() => toast.error("Erreur PayPal. Réessayez.")}
  />
);

const PayPalGate = ({ enabled, clientId, children }) =>
  enabled && clientId
    ? <PayPalScriptProvider options={{ clientId, currency: "USD", intent: "capture" }}>{children}</PayPalScriptProvider>
    : children;

export const CreditsPacks = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { user, setAuthOpen, refresh } = useAuth();
  const { t } = useLang();
  const [packs, setPacks] = useState([]);
  const [pending, setPending] = useState(null);
  const [paypal, setPaypal] = useState({ enabled: false, clientId: "" });

  useEffect(() => {
    getPacks().then(({ data }) => setPacks(data)).catch(() => {});
    paypalConfig().then(({ data }) => setPaypal({ enabled: data.enabled, clientId: data.client_id })).catch(() => {});
  }, []);

  const buy = async (lookup) => {
    if (!user) return setAuthOpen(true);
    try {
      setPending(lookup);
      await startCheckout(lookup);
    } catch {
      setPending(null);
      toast.error(t("contact.fail"));
    }
  };

  return (
    <section id="credits" data-testid="credits-section" className="relative py-28 lg:py-36">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(37,99,235,0.08),transparent_55%)]" />
      <div className="relative mx-auto max-w-[1200px] px-6 lg:px-10">
        <div ref={ref} className="mb-12 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} className="font-mono text-xs uppercase tracking-[0.35em] text-blue-400">
              [ {t("credits.tag")} ]
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-4 font-display text-4xl font-800 tracking-tighter text-white sm:text-5xl lg:text-6xl"
            >
              {t("credits.title")}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-5 font-body text-base leading-relaxed text-[#CBD5E1] sm:text-lg"
            >
              {t("credits.subtitle")}
            </motion.p>
          </div>
          {user && (
            <div className="flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-400/5 px-4 py-2">
              <Coins size={14} className="text-blue-300" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue-200">{t("credits.balance")} : {user.credits} {t("credits.credits")}</span>
            </div>
          )}
        </div>

        <PayPalGate enabled={paypal.enabled && !!user} clientId={paypal.clientId}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {packs.map((pack, i) => {
            const m = META[pack.lookup_key] || { icon: Coins, accent: "text-blue-300" };
            const Icon = m.icon;
            const loading = pending === pack.lookup_key;
            return (
              <motion.div
                key={pack.lookup_key}
                data-testid={`pack-card-${pack.lookup_key}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className={`relative flex flex-col rounded-2xl border p-8 backdrop-blur-xl transition-[border-color,box-shadow] duration-500 ${
                  m.featured ? "border-blue-400/50 bg-blue-500/[0.05] glow-cyan" : "border-blue-500/15 bg-blue-400/[0.02] hover:border-blue-400/40"
                }`}
              >
                {t(`credits.notes.${pack.lookup_key}`) && (
                  <span className="absolute right-6 top-6 rounded-full border border-blue-400/30 bg-blue-500/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-blue-200">{t(`credits.notes.${pack.lookup_key}`)}</span>
                )}
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl border border-blue-400/40 bg-blue-500/10`}>
                  <Icon size={19} className={m.accent} />
                </div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display text-4xl font-800 tracking-tighter text-white">{pack.credits}</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-blue-300">{t("credits.credits")}</span>
                </div>
                <p className="mt-1 font-body text-sm text-[#8AA0C0]">{pack.label}</p>
                <div className="mt-6 font-display text-2xl font-800 tracking-tighter text-white">${pack.price}</div>
                <button
                  data-testid={`pack-buy-${pack.lookup_key}`}
                  onClick={() => buy(pack.lookup_key)}
                  disabled={loading}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:scale-[1.02] hover:bg-blue-500 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Coins size={14} />}
                  {t("credits.buy")}
                </button>
                {user && paypal.enabled && (
                  <div className="mt-3" data-testid={`paypal-buttons-${pack.lookup_key}`}>
                    <div className="mb-2 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-[#607089]">— ou —</div>
                    <PayPalPackButton pack={pack} onDone={refresh} />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
        </PayPalGate>
      </div>
    </section>
  );
};
